import { useToast } from "@/components/ui/use-toast.ts";
import { fetchApi } from "@/lib/api/index.ts";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { RawRuleOf, MongoAbility } from "@casl/ability";

export type AbilityRule = RawRuleOf<MongoAbility>;

export enum UserRole {
  MENTOR = "MENTOR",
  MENTEE = "MENTEE",
  ADMIN = "ADMIN",
  SUPERADMIN = "SUPERADMIN",
}

interface UserMetadata {
  first_login?: string;
  last_survey_completed_on?: string;
  last_survey_id?: string;
  surveys?: {
    [key: string]: any;
  };
  schedule?: {
    isOnline: boolean;
    startTime: string;
    endTime: string;
    day: string;
  }[];
  timezone?: string;
  auto_detect_timezone?: boolean;
  notes?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    timestamp?: string;
    ip?: string;
    updated_at?: string;
    reason?: string;
  };
  account_overriding?: boolean;
  check_in_frequency?: string;
  lock_history?: LockHistoryEntry[];
  intercom_disabled?: boolean;
  onboarding?: {
    step?: number;

    screen?: string;
    ground_rules_accepted?: boolean;
    mentor_match_completed?: boolean;
    mentor_slot_cleanup_job_id?: string;

    mentor?: {
      id: string;
      name: string;
      profile_picture_url: string;
    };

    mental_health_issues?: string[];
    share_survey?: string;
    first_mentor_conversation?: string;
  };
  app_guide?: {
    access_records_step?: number;
    guide_step?: number;
    completed_at?: string;

    location_permission_asked?: boolean;
    profile_photo_modal_shown?: boolean;
  };
  developer?: boolean;
}

export interface LockHistoryEntry {
  locked_at: string;
  locked_reason: string;
  locked_by: string;
  unlocked_at?: string;
  unlocked_by?: string;
}

export interface Profile {
  id: string;
  user_id: string;
  bio?: string | null;
  sex?: string | null;
  race?: string | null;
  interests?: string[];
  struggles?: string[];
  life_experiences?: string[] | null;
  most_like_you?: string[];
  favorite_food?: string;
  favorite_music?: string;
  video_url?: string | null;
  audio_url?: string;
  created_at: Date;
  updated_at: Date;
  profile_audio_url?: string | null;
  interest_images?: Array<{ interest: string; imageUrl: string }> | null;
  match_card_photo_url?: string;
  metadata?: {
    proudMoment?: string;
    supportSystem?: string[];
    personalityTraits?: string[];
    parentConsent?: "Yes" | "No";
    parentOptOutReason?: string;
    mentorUnderstanding?: string;
    zipcode?: string;
    insuranceProvider?: string;
    state?: string;
    policies?: {
      terms?: boolean;
      privacy?: boolean;
      hipaa?: boolean;
      research?: boolean;
    };
    researchConsent?: {
      consent?: boolean;
      updatedAt?: string;
    };
    mentorPreferences?: {
      [key: string]: any;
    };
    additionalInfo?: string;
    interestDetails?: Record<string, string>;
    struggleDetails?: string;
    priorityConcerns?: {
      concerns?: string[];
      explanations?: any;
    };
  };
  redacted_fields?: string[];
}

export interface User {
  id: string;
  created_at: Date;
  updated_at: Date;
  stytch_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  phone: string | null;

  /* @deprecated Use systemRoles instead */
  role: UserRole;

  birthday?: Date | null;
  location?: string | null;
  profile_picture_url?: string | null;

  /* @deprecated Use systemRoles instead */
  is_supervisor?: boolean;

  pronouns?: string;
  locked?: boolean;
  support_url?: string | null;
  emergency_phone?: string | null;
  guardian_name?: string | null;
  stripe_id?: string | null;
  sendbird_access_token: string;
  wage?: number | null;
  deactivated?: boolean;

  metadata?: UserMetadata;
  assigned_groups?: {
    id: string;
    name: string;
  }[];
  profile?: Profile;
  unreadNotificationCount?: number;
  has_onboarded?: boolean;
  is_profile_complete?: boolean;
  profile_sections_count?: number;
  abilities?: AbilityRule[];
  systemRoles?: string[];
  redacted_fields?: string[];
}

interface MeResponse {
  user: User;
  abilities?: AbilityRule[];
  systemRoles?: string[];
}

export const useCurrentUser = () => {
  return useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const response = await fetchApi<MeResponse>({
        url: "/v2/users/me",
        method: "GET",
      });

      if (response.status === 401) throw new Error("Unauthorized");

      if (!response.data) throw new Error("No user found");

      return {
        ...response.data.user,
        abilities: response.data.abilities ?? response.data.user.abilities,
        systemRoles:
          response.data.systemRoles ?? response.data.user.systemRoles,
      };
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error?.response?.status === 401) {
        return false;
      }

      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const user = await fetchApi<{
        user: User;
        systemRoles: string[];
        abilities: AbilityRule[];
      }>({
        url: `/v2/users/${id}`,
        method: "GET",
      });

      if (!user.data) throw new Error("No user found");

      return {
        ...user.data.user,
        systemRoles: user.data.systemRoles ?? user.data.user.systemRoles,
        abilities: user.data.abilities ?? user.data.user.abilities,
      };
    },
    enabled: !!id,
  });
};

interface UserResponse {
  users: User[];
  isLastPage: boolean;
}

export const useUsers = (
  page: number,
  perPage: number,
  search?: string,
  customFilter?: string,
  group?: string,
  status?: string,
  profileCompleteness?: string,
  enabled = true,
  shouldKeepPreviousData = true,
) => {
  return useQuery<UserResponse>({
    queryKey: [
      "users",
      page,
      perPage,
      search,
      customFilter,
      group,
      status,
      profileCompleteness,
    ],
    queryFn: async () => {
      const response = await fetchApi<UserResponse>({
        url: "/v2/users",
        method: "GET",
        params: {
          page,
          limit: perPage,
          search,
          customFilter,
          group,
          status,
          profileCompleteness,
        },
      });

      if (!response.data) {
        throw new Error("No data returned from API");
      }

      return {
        users: response.data.users,
        isLastPage: response.data.isLastPage,
      };
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    enabled,
  });
};

export const createUserSchema = z.object({
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().min(10),
  role: z.enum([UserRole.MENTEE, UserRole.MENTOR]),
});

export type CreateUserSchema = z.infer<typeof createUserSchema>;

export const useCreateUser = () => {
  const { toast } = useToast();

  const queryClient = useQueryClient();
  return useMutation<User, Error, CreateUserSchema>({
    mutationKey: ["create-user"],
    mutationFn: async (data) => {
      const user = await fetchApi<{ user: User }>({
        url: "/v2/users",
        method: "POST",
        data: {
          ...data,
          name: `${data.firstName} ${data.lastName}`,
        },
      });

      return user.data.user;
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        if (error.response?.data?.message === "INVALID_PHONE_NUMBER") {
          description = "Invalid phone number";
        } else if (
          error.response?.data?.error?.error_type === "duplicate_email"
        ) {
          description = "Email already exists";
        } else if (
          error.response?.data?.error?.error_type === "duplicate_phone_number"
        ) {
          description = "Phone number already exists";
        } else {
          description = error.response?.data?.error?.message;
        }
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "User created successfully",
        variant: "default",
      });
    },
    onSettled: () => {
      // Invalidate the users query
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === "users";
        },
      });
    },
  });
};

export const updateUserSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  birthday: z
    .string()
    .transform((value) => new Date(value))
    .optional(),
  location: z.string().optional(),
  pronouns: z.string().optional(),
  locked: z.boolean().optional(),
  lockedReason: z.string().nullable().optional(),
  supportUrl: z.string().optional(),
  emergencyPhone: z.string().optional(),
  guardianName: z.string().optional(),
  wage: z.number().optional(),
  deactivated: z.boolean().optional(),
  role: z.enum([UserRole.ADMIN, UserRole.MENTOR]).optional(),
  is_supervisor: z.boolean().optional(),
  notes: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  profile: z
    .object({
      bio: z.string().optional(),
      sex: z.string().optional(),
      race: z.string().optional(),
      interests: z.array(z.string()).optional(),
      life_experiences: z.array(z.string()).optional(),
      most_like_you: z.array(z.string()).optional(),
      favorite_food: z.string().optional(),
      favorite_music: z.string().optional(),
      metadata: z
        .object({
          parentName: z.string().optional(),
          parentConsent: z.enum(["Yes", "No"]).optional(),
          parentOptOutReason: z.string().optional(),
          parentEmail: z.string().email().optional(),
          parentPhone: z.string().optional(),
          zipcode: z.string().optional(),
          insuranceProvider: z.string().optional(),
          interestDetails: z.record(z.string()).optional(),
          struggleDetails: z.string().optional(),
        })
        .optional(),
    })
    .optional(),

  metadata: z
    .object({
      onboarding: z
        .object({
          mental_health_issues: z.array(z.string()).optional(),
        })
        .optional(),
      intercom_disabled: z.boolean().optional(),
    })
    .optional(),
});

export type UpdateUserSchema = z.infer<typeof updateUserSchema>;

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation<
    User,
    Error,
    {
      userId: string;
      params: UpdateUserSchema;
    }
  >({
    mutationKey: ["update-user"],
    mutationFn: async ({ userId, params }) => {
      const user = await fetchApi<{ user: User }>({
        url: `/v2/users/${userId}`,
        method: "PATCH",
        data: params,
      });
      return user.data.user;
    },
    onSettled: (_, __, data) => {
      // Invalidate the users query
      queryClient.invalidateQueries({
        queryKey: ["user", data?.userId],
      });

      // If changing user status, invalidate mentorship queries as well
      // since all their mentorships will be deactivated
      if (data?.params.deactivated !== undefined) {
        // Invalidate user-specific mentorships
        queryClient.invalidateQueries({
          queryKey: ["user-mentorships", data.userId],
        });

        // Invalidate general mentorship queries
        queryClient.invalidateQueries({
          queryKey: ["mentorship"],
        });

        // Invalidate admin lite mentorships
        queryClient.invalidateQueries({
          queryKey: ["lite-mentorships"],
        });

        // Invalidate users list since deactivation status changed
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
      }
    },
  });
};

export const scheduleSchema = z.object({
  schedule: z.array(
    z.object({
      day: z.string(),
      isOnline: z.boolean(),
      startTime: z.string(),
      endTime: z.string(),
    }),
  ),
  timezone: z.string(),
  autoDetectTimezone: z.boolean().optional(),
});

export type ScheduleSchema = z.infer<typeof scheduleSchema>;

export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, ScheduleSchema>({
    mutationFn: async (data) => {
      const { autoDetectTimezone, ...rest } = data;
      await fetchApi({
        url: "/v2/users/schedule",
        method: "PATCH",
        data: {
          ...rest,
          auto_detect_timezone: autoDetectTimezone,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Schedule updated successfully",
      });
    },
    onError: (error) => {
      console.error("Failed to update schedule:", error);
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["current-user"],
      });
    },
  });
};

export const useUpdateUserSchedule = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, ScheduleSchema>({
    mutationFn: async (data) => {
      const { autoDetectTimezone, ...rest } = data;
      await fetchApi({
        url: `/v2/users/${userId}/schedule`,
        method: "PATCH",
        data: {
          ...rest,
          auto_detect_timezone: autoDetectTimezone,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Schedule updated successfully",
      });
    },
    onError: (error) => {
      console.error("Failed to update schedule:", error);
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", userId],
      });
    },
  });
};

export const useAssignGroups = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    User,
    Error,
    {
      userId: string;
      groupIds: string[];
    }
  >({
    mutationFn: async ({ userId, groupIds }) => {
      const response = await fetchApi<{ user: User }>({
        url: `/v2/users/${userId}/assign-groups`,
        method: "PATCH",
        data: { groupIds },
      });
      return response.data.user;
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Groups assigned successfully",
      });
      queryClient.setQueryData(["user", data.id], data);
    },
    onError: (error) => {
      console.error("Failed to assign groups:", error);
      toast({
        title: "Error",
        description: "Failed to assign groups",
        variant: "destructive",
      });
    },
  });
};

export const useRemoveSupervisorFromMentorships = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    { message: string; affectedMentorships: number },
    Error,
    { userId: string }
  >({
    mutationFn: async ({ userId }) => {
      const response = await fetchApi<{
        message: string;
        affectedMentorships: number;
      }>({
        url: `/v2/users/${userId}/remove-supervisor`,
        method: "POST",
      });
      return response.data;
    },
    onSuccess: (data, { userId }) => {
      toast({
        title: "Success",
        description: `Supervisor removed from ${data.affectedMentorships} mentorship(s)`,
      });
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ["user", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["supervisorMentorships", userId],
      });
    },
    onError: (error) => {
      console.error("Failed to remove supervisor from mentorships:", error);
      toast({
        title: "Error",
        description: "Failed to remove supervisor from mentorships",
        variant: "destructive",
      });
    },
  });
};

interface SupervisorChat {
  id: string;
  sendbird_room_id: string;
}

export const useGetSupervisedChannels = () => {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: ["supervisedChannels", user?.id],
    queryFn: async () => {
      const response = await fetchApi<{ channels: SupervisorChat[] }>({
        url: `/v2/users/${user?.id}/supervised-channels`,
        method: "GET",
      });

      return response.data?.channels;
    },
    enabled: !!user?.id && user?.is_supervisor,
  });
};

export interface SupervisorMentor {
  mentor_id: string;
  mentor_name: string;
  mentor_email: string;
  mentor_profile_picture_url: string;
}

export const useGetUserSupervisorMentors = (
  userId: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["supervisorMentors", userId],
    queryFn: () =>
      fetchApi<{ mentors: SupervisorMentor[] }>({
        url: `/v2/users/${userId}/supervised-mentors`,
        method: "GET",
      }).then((res) => res.data),
    enabled,
  });
};

interface UserSupervisorAndChannel {
  supervisor: User;
  channel: SupervisorChat;
}

export const useGetUserSupervisorAndChannel = (
  userId: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["userSupervisorAndChannel", userId],
    queryFn: async () => {
      const { data } = await fetchApi<UserSupervisorAndChannel>({
        url: `/v2/users/${userId}/supervisor`,
        method: "GET",
      });

      return data;
    },
    enabled,
    retry: false,
  });
};

interface MentorSlotConfig {
  id: string;
  mentor_id: string;
  max_total_slots: number;
  max_daily_slots: number;
  slots_used_today: number;
  last_reset_date: string;
  created_at: string;
  updated_at: string;
}

interface MentorSlotsUpdateData {
  max_total_slots: number;
  max_daily_slots: number;
}

export const useUpdateMentorSlots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: MentorSlotsUpdateData;
    }) => {
      const response = await fetchApi<{ slot_config: MentorSlotConfig }>({
        url: `/v2/users/${userId}/mentor-slots`,
        method: "PATCH",
        data,
      });
      return response.data.slot_config;
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["mentor-slots", userId] });
    },
  });
};

export const useMentorSlots = (userId: string) => {
  return useQuery({
    queryKey: ["mentor-slots", userId],
    queryFn: async () => {
      const response = await fetchApi<{ slot_config: MentorSlotConfig }>({
        url: `/v2/users/${userId}/mentor-slots`,
        method: "GET",
      });
      return response.data.slot_config;
    },
    enabled: !!userId,
  });
};

export function useUserAddress(userId: string) {
  const { data: user } = useUser(userId);

  return useQuery({
    queryKey: ["userAddress", userId],
    queryFn: async () => {
      if (!user) return { address: "Address not available", isFallback: true };

      if (
        user.metadata?.location?.latitude &&
        user.metadata?.location?.longitude
      ) {
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${user.metadata.location.latitude},${user.metadata.location.longitude}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`,
          );
          const data = await response.json();

          if (data.status === "OK" && data.results?.[0]?.formatted_address) {
            return {
              address: data.results[0].formatted_address,
              isFallback: false,
            };
          }
        } catch (error) {
          console.error("Error fetching address from coordinates:", error);
        }
      }

      return {
        address: user.location || "Address not available",
        isFallback: true,
        reason:
          user.metadata?.location?.reason || "Current location not available",
      };
    },
    enabled: !!user,
  });
}

export function useUpdateProfilePhoto(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      console.log("file", file, formData);

      const response = await fetchApi<{ user: User }>({
        url: `/v2/users/${userId}/profile_picture`,
        method: "PATCH",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data], // Prevent axios from trying to transform FormData
      });

      return response.data.user;
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ["users", user.id] });
      toast({
        title: "Success",
        description: "Profile photo updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating profile photo:", error);
      toast({
        title: "Error",
        description: "Failed to update profile photo. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdatePhoneNumber(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      const response = await fetchApi<{ user: User }>({
        url: `/v2/users/${userId}/phone`,
        method: "PATCH",
        data: { phoneNumber: phoneNumber },
      });
      return response.data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", userId] });
      toast({
        title: "Success",
        description: "Phone number updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating phone number:", error);
      toast({
        title: "Error",
        description: "Failed to update phone number. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateEmail(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (email: string) => {
      const response = await fetchApi<{ user: User }>({
        url: `/v2/users/${userId}/email`,
        method: "PATCH",
        data: { email },
      });
      return response.data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", userId] });
      toast({
        title: "Success",
        description: "Email updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating email:", error);
      toast({
        title: "Error",
        description: "Failed to update email. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateProfileVideo(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchApi<{ message: string }>({
        url: `/v2/users/${userId}/profile_video`,
        method: "PATCH",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", userId] });
      toast({
        title: "Success",
        description: "Profile video updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating profile video:", error);
      toast({
        title: "Error",
        description: "Failed to update profile video. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateProfileAudio(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchApi<{ message: string }>({
        url: `/v2/users/${userId}/profile_audio`,
        method: "PATCH",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", userId] });
      toast({
        title: "Success",
        description: "Profile audio updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating profile audio:", error);
      toast({
        title: "Error",
        description: "Failed to update profile audio. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export interface MentorSlot {
  mentor: {
    id: string;
    name: string;
    profile_picture_url: string;
    role: string;
  };
  slot_config: {
    max_total_slots: number;
    max_daily_slots: number;
    slots_used_today: number;
  };
  active_mentorships_count: number;
}

export const useGetAllMentorSlots = () => {
  return useQuery({
    queryKey: ["mentor-slots-all"],
    queryFn: async () => {
      const { data } = await fetchApi<{ mentor_slots: MentorSlot[] }>({
        url: "/v2/users/mentor-slots/all",
        method: "GET",
      });
      return data.mentor_slots;
    },
  });
};

export const useUpdateAllMentorSlots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      maxTotalSlots,
      maxDailySlots,
    }: {
      maxTotalSlots: number;
      maxDailySlots: number;
    }) => {
      const { data } = await fetchApi({
        url: "/v2/users/mentor-slots/batch",
        method: "PATCH",
        data: {
          max_total_slots: maxTotalSlots,
          max_daily_slots: maxDailySlots,
        },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor-slots-all"] });
    },
  });
};

export interface MentorScheduleItem {
  day: string;
  isOnline: boolean;
  startTime: string;
  endTime: string;
}

export interface MentorSchedule {
  mentor: {
    id: string;
    name: string;
    profile_picture_url: string;
  };
  schedule: MentorScheduleItem[];
  timezone: string;
  auto_detect_timezone: boolean;
}

export const useGetAllMentorSchedules = () => {
  return useQuery({
    queryKey: ["mentor-schedules-all"],
    queryFn: async () => {
      const { data } = await fetchApi<{ mentor_schedules: MentorSchedule[] }>({
        url: "/v2/users/mentor-schedules/all",
        method: "GET",
      });
      return data.mentor_schedules;
    },
  });
};

export const useIsRematched = (userId: string) => {
  return useQuery({
    queryKey: ["is-rematched", userId],
    queryFn: async () => {
      const response = await fetchApi<{ isRematched: boolean }>({
        url: `/v2/users/${userId}/is-rematched`,
        method: "GET",
      });

      return response.data.isRematched;
    },
    enabled: !!userId,
  });
};

export const useClearUserCache = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["clear-user-cache"],
    mutationFn: async (userId: string) => {
      const response = await fetchApi<{
        success: boolean;
        message: string;
        clearedUserId: string;
      }>({
        url: `/v2/users/${userId}/cache`,
        method: "DELETE",
      });

      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: "Cache cleared",
        description: data.message,
      });
      // Invalidate user-related queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: ["user", data.clearedUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },
    onError: (error) => {
      console.error("Failed to clear user cache:", error);
      toast({
        title: "Failed to clear cache",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

export const useDevCleanupUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["dev-cleanup-user"],
    mutationFn: async (userId: string) => {
      const response = await fetchApi<{
        success: boolean;
        message: string;
      }>({
        url: `/v2/users/${userId}/dev-cleanup`,
        method: "DELETE",
      });

      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: "User reset",
        description: data.message,
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["mentorship"],
      });
    },
    onError: (error) => {
      console.error("Failed to reset user:", error);
      toast({
        title: "Failed to reset user",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

export const useCreatePrivilegedUser = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      hash,
      email,
      phoneNumber,
      hashedPassword,
    }: {
      hash: string;
      email: string;
      phoneNumber: string;
      hashedPassword: string;
    }) => {
      const response = await fetchApi<{ user: User }>({
        url: `/v2/users/create-privileged-user`,
        method: "POST",
        data: { hash, email, phoneNumber, hashedPassword },
      });
      return response.data.user;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Password added successfully",
      });
      setTimeout(() => {
        navigate({ to: "/login" });
      }, 1000);
    },
    onError: (error) => {
      console.error("Error adding Password:", error);

      if (error instanceof AxiosError && error.response?.status === 400) {
        toast({
          title: "Error",
          description: "A password has already been added to this account",
          variant: "destructive",
        });
        return;
      }

      if (error instanceof AxiosError && error.response?.status === 429) {
        toast({
          title: "Error",
          description:
            "Too many failed attempts -- please wait 10 minutes and try again later",
        });
        return;
      }

      toast({
        title: "Error",
        description: "Failed to add Password. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export function useUploadMatchCardPhoto(userId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchApi<{
        message: string;
        match_card_photo_url: string;
      }>({
        url: `/v2/users/${userId}/match_card_photo`,
        method: "PATCH",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      toast({
        title: "Success",
        description: "Match card photo uploaded successfully",
      });
    },
    onError: (error) => {
      console.error("Error uploading match card photo:", error);
      toast({
        title: "Error",
        description: "Failed to upload match card photo. Please try again.",
        variant: "destructive",
      });
    },
  });
}
